import React, { useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useAuth } from '../auth/AuthProvider';
import { colors, radii, shadows, neu, fonts } from '../domain/tokens';

export function LoginScreen() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!email.trim() || !password) return;
    setBusy(true);
    setError(null);
    const message = await signIn(email.trim(), password);
    if (message) setError(message);
    setBusy(false);
  };

  return (
    <KeyboardAvoidingView
      style={styles.root}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <View style={styles.card}>
        <Text style={styles.title}>OLMPS</Text>
        <Text style={styles.subtitle}>Sign in to SyQi</Text>
        <TextInput
          style={styles.input}
          placeholder="Email"
          placeholderTextColor={colors.slate}
          autoCapitalize="none"
          autoComplete="email"
          keyboardType="email-address"
          value={email}
          onChangeText={setEmail}
        />
        <TextInput
          style={styles.input}
          placeholder="Password"
          placeholderTextColor={colors.slate}
          secureTextEntry
          value={password}
          onChangeText={setPassword}
          onSubmitEditing={submit}
        />
        {error ? <Text style={styles.error}>{error}</Text> : null}
        <Pressable style={[styles.button, busy && styles.buttonDisabled]} onPress={submit} disabled={busy}>
          {busy ? (
            <ActivityIndicator color={colors.blueText} />
          ) : (
            <Text style={styles.buttonText}>Sign In</Text>
          )}
        </Pressable>
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.bgTop,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  card: {
    width: '100%',
    maxWidth: 380,
    backgroundColor: colors.glassFill,
    borderRadius: radii.shell,
    padding: 28,
    ...neu.raisedLg,
  },
  title: {
    fontSize: 32,
    fontWeight: '900',
    color: colors.blueText,
    textAlign: 'center',
    letterSpacing: 2,
    fontFamily: fonts.title,
    textTransform: 'uppercase',
  },
  subtitle: {
    fontSize: 14,
    color: colors.slate,
    textAlign: 'center',
    marginBottom: 24,
    marginTop: 4,
  },
  input: {
    backgroundColor: colors.inputFill,
    borderRadius: radii.tile,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    color: colors.text,
    marginBottom: 12,
    ...neu.insetDeep,
  },
  error: {
    color: colors.danger,
    marginBottom: 12,
    textAlign: 'center',
  },
  button: {
    backgroundColor: colors.lime,
    borderRadius: radii.pill,
    paddingVertical: 14,
    alignItems: 'center',
    ...neu.inset,
  },
  buttonDisabled: { opacity: 0.6 },
  buttonText: {
    fontWeight: '900',
    fontSize: 16,
    color: colors.blueText,
    letterSpacing: 1,
  },
});
